import { useCallback } from 'react';
import { type WorkflowNodeInput } from '@cotera/api';
import { useWhoami } from '@cotera/client/app/stores/org';
import { toast } from '@cotera/client/app/components/ui';
import { humanReadableSchedule } from '../trigger/schedule-utils';
import { useWorkflowTriggerOptions } from '../trigger/hooks';
import { useWorkflowModel } from './workflow.model';
import { useQueryClient } from '@tanstack/react-query';
import {
  type DraftConnectFrom,
  draftAddAgentAsForEachBody,
  draftAddAgentNode,
  draftAddCronTrigger,
  draftAddEmitEventNode,
  draftAddMapperNode,
  draftAddBucketNode,
  draftAddGroupByNode,
  draftAddEventTrigger,
  draftAddFilterAsForEachBody,
  draftAddDebounceAsForEachBody,
  draftAddFilterNode,
  draftAddDebounceNode,
  draftAdoptForEachBody,
  draftAddForEachNode,
  draftAddRunWorkflowAsForEachBody,
  draftAddRunWorkflowNode,
  draftAddToolCallAsForEachBody,
  draftAddToolCallNode,
  draftAddWebhookTrigger,
  draftAddWorkflowEdge,
  draftArchiveTriggerBinding,
  draftBindExistingTrigger,
  draftDeleteWorkflowEdge,
  draftDeleteWorkflowNode,
  draftSetTriggerBindingEnabled,
  draftUpdateCronTrigger,
  draftUpdateEventTrigger,
  draftUpdateNodeData,
  draftUpdateWebhookTrigger,
  draftUpdateWorkflowEdge,
} from './workflow-draft-graph';
import type { WorkflowNodePaletteSelection } from './components/workflow-node-palette';

export function useWorkflowGraphMutations(): {
  canEdit: boolean;
  refreshGraph: () => void;
  addAgentNode: (
    agentId: string,
    agentName: string,
    connectFrom?: DraftConnectFrom
  ) => Promise<void>;
  addForEachNode: (connectFrom?: DraftConnectFrom) => Promise<void>;
  addFilterNode: (connectFrom?: DraftConnectFrom) => Promise<void>;
  addDebounceNode: (connectFrom?: DraftConnectFrom) => Promise<void>;
  addEmitEventNode: (connectFrom?: DraftConnectFrom) => Promise<void>;
  addMapperNode: (connectFrom?: DraftConnectFrom) => Promise<void>;
  addBucketNode: (connectFrom?: DraftConnectFrom) => Promise<void>;
  addGroupByNode: (connectFrom?: DraftConnectFrom) => Promise<void>;
  addRunWorkflowNode: (
    targetWorkflowId: string,
    workflowName: string,
    connectFrom?: DraftConnectFrom
  ) => Promise<void>;
  addToolCallNode: (
    toolKey: string,
    toolName: string,
    connectFrom?: DraftConnectFrom
  ) => Promise<void>;
  /** Adds a node connected from a for-each and stamps it as the loop body. */
  addForEachBodyNode: (
    forEachNodeId: string,
    selection: WorkflowNodePaletteSelection
  ) => Promise<void>;
  /** Moves an existing for-each successor "inside" the loop as its body. */
  adoptForEachBodyNode: (
    forEachNodeId: string,
    bodyNodeId: string
  ) => Promise<void>;
  updateNodeData: (nodeId: string, node: WorkflowNodeInput) => Promise<void>;
  /** Resolves true on success, false if the request failed (already toasted). */
  addCronTrigger: (
    schedule: string,
    timezone?: string,
    name?: string
  ) => Promise<boolean>;
  /** Resolves true on success, false if the request failed (already toasted). */
  addEventTrigger: (
    name: string,
    eventName: string
  ) => Promise<{ success: true; triggerId: string } | { success: false }>;
  /** Resolves true on success, false if the request failed (already toasted). */
  addWebhookTrigger: (params: {
    name?: string;
    providerKey?: import('@cotera/client/app/etc/api/typed-client').WorkflowWebhookProviderKey;
    verificationConfig?: unknown;
    eventKey?: string;
    eventConfig?: unknown;
    endpointSecret?: string;
  }) => Promise<
    { success: true; triggerId: string; secret: string } | { success: false }
  >;
  /** Resolves true on success, false if the request failed (already toasted). */
  updateWebhookTrigger: (params: {
    bindingId: string;
    name?: string;
    webhookRowEnabled?: boolean;
    providerKey?: import('@cotera/client/app/etc/api/typed-client').WorkflowWebhookProviderKey;
    verificationConfig?: unknown;
    eventKey?: string;
    eventConfig?: unknown;
    endpointSecret?: string;
  }) => Promise<boolean>;
  /** Resolves true on success, false if the request failed (already toasted). */
  updateCronTrigger: (params: {
    bindingId: string;
    name?: string;
    schedule?: string;
    timezone?: string;
    cronRowEnabled?: boolean;
  }) => Promise<boolean>;
  /** Resolves true on success, false if the request failed (already toasted). */
  updateEventTrigger: (params: {
    bindingId: string;
    name?: string;
    eventName?: string;
    eventRowEnabled?: boolean;
  }) => Promise<boolean>;
  bindExistingTrigger: (triggerId: string) => Promise<boolean>;
  deleteWorkflowNode: (nodeId: string) => Promise<void>;
  deleteWorkflowEdge: (edgeId: string) => Promise<void>;
  addWorkflowEdge: (fromNodeId: string, toNodeId: string) => Promise<void>;
  updateWorkflowEdge: (
    edgeId: string,
    update: { fromNodeId?: string; toNodeId?: string }
  ) => Promise<void>;
  /**
   * The canvas card's on/off switch. Pauses one workflow ↔ trigger binding
   * without unbinding it, so the trigger keeps firing for any other workflow
   * bound to the same (org-deduped) trigger row.
   */
  setTriggerBindingEnabled: (params: {
    bindingId: string;
    enabled: boolean;
  }) => Promise<void>;
  /** Resolves true on success, false if the request failed (already toasted). */
  archiveTriggerBinding: (bindingId: string) => Promise<boolean>;
} {
  const model = useWorkflowModel();
  const canEdit = true;
  const queryClient = useQueryClient();
  const orgId = useWhoami((state) => state.org.id);
  const triggerOptions = useWorkflowTriggerOptions();

  const refreshGraph = useCallback(() => {
    model.refreshGraph();
  }, [model]);

  const addAgentNode = useCallback(
    async (
      agentId: string,
      agentName: string,
      connectFrom?: DraftConnectFrom
    ) => {
      model.updateDraftState((state) =>
        draftAddAgentNode(
          state,
          model.getDraftContext(),
          agentId,
          agentName,
          connectFrom
        )
      );
    },
    [model]
  );

  const addForEachNode = useCallback(
    async (connectFrom?: DraftConnectFrom) => {
      model.updateDraftState((state) =>
        draftAddForEachNode(state, model.getDraftContext(), connectFrom)
      );
    },
    [model]
  );

  const addFilterNode = useCallback(
    async (connectFrom?: DraftConnectFrom) => {
      model.updateDraftState((state) =>
        draftAddFilterNode(state, model.getDraftContext(), connectFrom)
      );
    },
    [model]
  );

  const addDebounceNode = useCallback(
    async (connectFrom?: DraftConnectFrom) => {
      model.updateDraftState((state) =>
        draftAddDebounceNode(state, model.getDraftContext(), connectFrom)
      );
    },
    [model]
  );

  const addEmitEventNode = useCallback(
    async (connectFrom?: DraftConnectFrom) => {
      model.updateDraftState((state) =>
        draftAddEmitEventNode(state, model.getDraftContext(), connectFrom)
      );
    },
    [model]
  );

  const addMapperNode = useCallback(
    async (connectFrom?: DraftConnectFrom) => {
      model.updateDraftState((state) =>
        draftAddMapperNode(state, model.getDraftContext(), connectFrom)
      );
    },
    [model]
  );

  const addBucketNode = useCallback(
    async (connectFrom?: DraftConnectFrom) => {
      model.updateDraftState((state) =>
        draftAddBucketNode(state, model.getDraftContext(), connectFrom)
      );
    },
    [model]
  );

  const addGroupByNode = useCallback(
    async (connectFrom?: DraftConnectFrom) => {
      model.updateDraftState((state) =>
        draftAddGroupByNode(state, model.getDraftContext(), connectFrom)
      );
    },
    [model]
  );

  const addRunWorkflowNode = useCallback(
    async (
      targetWorkflowId: string,
      workflowName: string,
      connectFrom?: DraftConnectFrom
    ) => {
      model.updateDraftState((state) =>
        draftAddRunWorkflowNode(
          state,
          model.getDraftContext(),
          targetWorkflowId,
          workflowName,
          connectFrom
        )
      );
    },
    [model]
  );

  const addToolCallNode = useCallback(
    async (
      toolKey: string,
      toolName: string,
      connectFrom?: DraftConnectFrom
    ) => {
      model.updateDraftState((state) =>
        draftAddToolCallNode(
          state,
          model.getDraftContext(),
          toolKey,
          toolName,
          connectFrom
        )
      );
    },
    [model]
  );

  const addForEachBodyNode = useCallback(
    async (forEachNodeId: string, selection: WorkflowNodePaletteSelection) => {
      const ctx = model.getDraftContext();
      model.updateDraftState((state) => {
        if (selection.kind === 'agent') {
          return draftAddAgentAsForEachBody(
            state,
            ctx,
            forEachNodeId,
            selection.agentId,
            selection.agentName
          );
        }
        if (selection.kind === 'tool-call') {
          return draftAddToolCallAsForEachBody(
            state,
            ctx,
            forEachNodeId,
            selection.toolKey,
            selection.toolName
          );
        }
        if (selection.kind === 'filter') {
          return draftAddFilterAsForEachBody(state, ctx, forEachNodeId);
        }
        if (selection.kind === 'debounce') {
          return draftAddDebounceAsForEachBody(state, ctx, forEachNodeId);
        }
        if (selection.kind === 'run-workflow') {
          return draftAddRunWorkflowAsForEachBody(
            state,
            ctx,
            forEachNodeId,
            selection.targetWorkflowId,
            selection.workflowName
          );
        }
        // Nested for-each as a loop body is not supported yet.
        return state;
      });
    },
    [model]
  );

  const adoptForEachBodyNode = useCallback(
    async (forEachNodeId: string, bodyNodeId: string) => {
      model.updateDraftState((state) =>
        draftAdoptForEachBody(state, forEachNodeId, bodyNodeId)
      );
    },
    [model]
  );

  const updateNodeData = useCallback(
    async (nodeId: string, node: WorkflowNodeInput) => {
      model.updateDraftState((state) =>
        draftUpdateNodeData(state, nodeId, node)
      );
    },
    [model]
  );

  const addCronTrigger = useCallback(
    async (
      schedule: string,
      timezone?: string,
      name?: string
    ): Promise<boolean> => {
      const resolvedName =
        name !== undefined && name.trim() !== ''
          ? name.trim()
          : humanReadableSchedule(schedule);
      model.updateDraftState((state) =>
        draftAddCronTrigger(
          state,
          model.getDraftContext(),
          schedule,
          timezone ?? 'UTC',
          resolvedName
        )
      );
      return true;
    },
    [model]
  );

  const addEventTrigger = useCallback(
    async (
      name: string,
      eventName: string
    ): Promise<{ success: true; triggerId: string } | { success: false }> => {
      const trimmedName = name.trim();
      const trimmedEventName = eventName.trim();
      if (trimmedName === '' || trimmedEventName === '') {
        toast.error('Name and event name are required');
        return { success: false };
      }
      const draftResult = draftAddEventTrigger(
        model.getDraftState(),
        model.getDraftContext(),
        trimmedName,
        trimmedEventName
      );
      model.updateDraftState(() => draftResult.state);
      return { success: true, triggerId: draftResult.triggerId };
    },
    [model]
  );

  const addWebhookTrigger = useCallback(
    async (params: {
      name?: string;
      providerKey?: import('@cotera/client/app/etc/api/typed-client').WorkflowWebhookProviderKey;
      verificationConfig?: unknown;
      eventKey?: string;
      eventConfig?: unknown;
      endpointSecret?: string;
    }): Promise<
      { success: true; triggerId: string; secret: string } | { success: false }
    > => {
      const createParams = {
        name: params.name ?? 'Webhook',
        providerKey: params.providerKey,
        verificationConfig: params.verificationConfig,
        eventKey: params.eventKey,
        eventConfig: params.eventConfig,
        endpointSecret: params.endpointSecret,
      };
      const draftResult = draftAddWebhookTrigger(
        model.getDraftState(),
        model.getDraftContext(),
        createParams
      );
      model.updateDraftState(() => draftResult.state);
      return {
        success: true,
        triggerId: draftResult.triggerId,
        secret: draftResult.secret,
      };
    },
    [model]
  );

  const updateWebhookTrigger = useCallback(
    async (params: {
      bindingId: string;
      name?: string;
      webhookRowEnabled?: boolean;
      providerKey?: import('@cotera/client/app/etc/api/typed-client').WorkflowWebhookProviderKey;
      verificationConfig?: unknown;
      eventKey?: string;
      eventConfig?: unknown;
      endpointSecret?: string;
    }): Promise<boolean> => {
      model.updateDraftState((state) =>
        draftUpdateWebhookTrigger(state, params)
      );
      void queryClient.invalidateQueries({
        queryKey: [orgId, 'webhookTriggers', 'settings'],
      });
      return true;
    },
    [model, queryClient, orgId]
  );

  const updateCronTrigger = useCallback(
    async (params: {
      bindingId: string;
      name?: string;
      schedule?: string;
      timezone?: string;
      cronRowEnabled?: boolean;
    }): Promise<boolean> => {
      model.updateDraftState((state) => draftUpdateCronTrigger(state, params));
      return true;
    },
    [model]
  );

  const updateEventTrigger = useCallback(
    async (params: {
      bindingId: string;
      name?: string;
      eventName?: string;
      eventRowEnabled?: boolean;
    }): Promise<boolean> => {
      model.updateDraftState((state) => draftUpdateEventTrigger(state, params));
      return true;
    },
    [model]
  );

  const bindExistingTrigger = useCallback(
    async (triggerId: string): Promise<boolean> => {
      const trigger = triggerOptions.find((option) => option.id === triggerId);
      if (trigger === undefined) {
        toast.error('Trigger not found');
        return false;
      }
      model.updateDraftState((state) =>
        draftBindExistingTrigger(state, model.getDraftContext(), trigger)
      );
      return true;
    },
    [model, triggerOptions]
  );

  const deleteWorkflowNode = useCallback(
    async (nodeId: string) => {
      model.updateDraftState((state) => draftDeleteWorkflowNode(state, nodeId));
    },
    [model]
  );

  const deleteWorkflowEdge = useCallback(
    async (edgeId: string) => {
      model.updateDraftState((state) => draftDeleteWorkflowEdge(state, edgeId));
    },
    [model]
  );

  const addWorkflowEdge = useCallback(
    async (fromNodeId: string, toNodeId: string) => {
      model.updateDraftState((state) =>
        draftAddWorkflowEdge(
          state,
          model.getDraftContext(),
          fromNodeId,
          toNodeId
        )
      );
    },
    [model]
  );

  const updateWorkflowEdge = useCallback(
    async (
      edgeId: string,
      update: { fromNodeId?: string; toNodeId?: string }
    ) => {
      model.updateDraftState((state) =>
        draftUpdateWorkflowEdge(state, edgeId, update)
      );
    },
    [model]
  );

  const setTriggerBindingEnabled = useCallback(
    async (params: { bindingId: string; enabled: boolean }): Promise<void> => {
      model.updateDraftState((state) =>
        draftSetTriggerBindingEnabled(state, params)
      );
    },
    [model]
  );

  const archiveTriggerBinding = useCallback(
    async (bindingId: string): Promise<boolean> => {
      model.updateDraftState((state) =>
        draftArchiveTriggerBinding(state, bindingId)
      );
      return true;
    },
    [model]
  );

  return {
    canEdit,
    refreshGraph,
    addAgentNode,
    addForEachNode,
    addFilterNode,
    addDebounceNode,
    addEmitEventNode,
    addMapperNode,
    addBucketNode,
    addGroupByNode,
    addRunWorkflowNode,
    addToolCallNode,
    addForEachBodyNode,
    adoptForEachBodyNode,
    updateNodeData,
    addCronTrigger,
    addEventTrigger,
    addWebhookTrigger,
    updateWebhookTrigger,
    updateCronTrigger,
    updateEventTrigger,
    bindExistingTrigger,
    deleteWorkflowNode,
    deleteWorkflowEdge,
    addWorkflowEdge,
    updateWorkflowEdge,
    setTriggerBindingEnabled,
    archiveTriggerBinding,
  };
}
